import { z } from "zod";

/** 买家可展示的内容；转人工诊断和未知块的内部数据不进入视图模型。 */
type ContentBlock =
  | { type: "text"; text: string }
  | { type: "image"; url: string; alt: string }
  | { type: "transfer" }
  | { type: "unknown" };

/** 块在不可变回复中的位置就是其视图标识，不生成随机 ID。 */
export type ReplyContentBlock = ContentBlock & { id: string };

const blockEnvelope = z.looseObject({ type: z.string() });
const textBlock = blockEnvelope.extend({
  text: z.array(z.looseObject({ value: z.string() })).min(1),
});
const pictureBlock = blockEnvelope.extend({
  text: z
    .array(
      z.looseObject({
        value: z.string(),
        meta_data: z
          .looseObject({
            pic: z.looseObject({ value: z.string() }).optional(),
          })
          .nullish(),
      }),
    )
    .min(1),
});

function readBlock(raw: unknown): ContentBlock[] {
  const envelope = blockEnvelope.safeParse(raw);
  if (!envelope.success) return [{ type: "unknown" }];
  if (envelope.data.type === "transfer") return [{ type: "transfer" }];
  if (envelope.data.type === "text") {
    const parsed = textBlock.safeParse(raw);
    return parsed.success
      ? [
          {
            type: "text",
            text: parsed.data.text.map((item) => item.value).join("\n"),
          },
        ]
      : [{ type: "unknown" }];
  }
  if (envelope.data.type === "picture" || envelope.data.type === "pic") {
    const parsed = pictureBlock.safeParse(raw);
    if (!parsed.success) return [{ type: "unknown" }];
    return parsed.data.text.map((item): ContentBlock => {
      const url = item.meta_data?.pic?.value ?? item.value;
      const validUrl = z.url({ protocol: /^https?$/ }).safeParse(url);
      if (!validUrl.success) return { type: "unknown" };
      return {
        type: "image",
        url: validUrl.data,
        alt: item.meta_data?.pic ? item.value : "",
      };
    });
  }
  return [{ type: "unknown" }];
}

/**
 * Socket、历史和 Inbox 共用的内容读取：保留消息块顺序。
 * 只把带 type 的数组识别为消息，普通 JSON 正文不会被误当协议。
 * 本函数不从文案或块类型制造 transfer_reason；原因映射属于后端接入层。
 */
export function readReplyContent(content: string): {
  structured: boolean;
  blocks: ReplyContentBlock[];
} {
  let raw: unknown;
  try {
    raw = JSON.parse(content) as unknown;
  } catch {
    return {
      structured: false,
      blocks: content ? [{ id: "0", type: "text", text: content }] : [],
    };
  }
  if (
    !Array.isArray(raw) ||
    (raw.length > 0 &&
      !raw.some(
        (item: unknown) =>
          typeof item === "object" &&
          item !== null &&
          Object.hasOwn(item, "type"),
      ))
  ) {
    return {
      structured: false,
      blocks: content ? [{ id: "0", type: "text", text: content }] : [],
    };
  }
  return {
    structured: true,
    blocks: raw
      .flatMap((item: unknown) => readBlock(item))
      .map((block, index) => ({ ...block, id: String(index) })),
  };
}
